/** @jsx jsx */
import type { SerializedStyles } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as React from 'react';
import type { ReactElement } from 'react';
import { useSelector } from 'react-redux';

import type { AiCoach_RespondToUserMessageInput as AiCoachRespondToUserMessageInput } from '__generated__/graphql-types';
import type { ApolloError } from 'apollo-client';

import { useLocation } from 'js/lib/useRouter';

import { breakpoints } from '@coursera/cds-core';

import type { ProgressiveLoaderProps } from 'bundles/ai-coach-platform/components/building-blocks';
import type { HideMessageType } from 'bundles/ai-coach-platform/components/patterns/chat/ChatMessages';
import Conversation, { type ConversationProps } from 'bundles/ai-coach-platform/components/patterns/chat/Conversation';
import { useChatHistory } from 'bundles/ai-coach-platform/components/patterns/chat/ConversationHistory';
import NewConversationModal from 'bundles/ai-coach-platform/components/patterns/chat/NewConversationModal';
import type { ChatMessageBodyProps, ChatMessageProps } from 'bundles/ai-coach-platform/components/patterns/chat/common';
import type { ChatPopupButton } from 'bundles/ai-coach-platform/components/patterns/chat/common/ChatHeader';
import {
  CoachChatContextProvider,
  useCoachChatContext,
} from 'bundles/ai-coach-platform/components/patterns/chat/context/CoachChatContext';
import { useFetchChatHistoryOnVisibilityChange } from 'bundles/ai-coach-platform/components/patterns/chat/hooks/useFetchChatHistoryOnVisibilityChange';
import type { MemoryNotificationConfig } from 'bundles/ai-coach-platform/components/patterns/chat/notifications/types';
import { FindInChatSearch, SearchFooter } from 'bundles/ai-coach-platform/components/patterns/chat/search';
import { SettingsContextProvider } from 'bundles/ai-coach-platform/components/patterns/chat/settings/SettingsContextProvider';
import SettingsHome from 'bundles/ai-coach-platform/components/patterns/chat/settings/SettingsHome';
import StoreProviderWrapper from 'bundles/ai-coach-platform/components/patterns/chat/store/StoreProviderWrapper';
import type { UpdateChatHistory } from 'bundles/ai-coach-platform/components/patterns/chat/store/actions/types';
import {
  addResponseMessage,
  clearChatSession,
  clearContinuousChatSession,
  sendMessage as dispatchSendMessage,
  dropMessages,
  enableMsgLoader,
  setChatSessionId,
  setChatSessionStorageKey,
  setContinuousChatSessionId,
  setContinuousChatSessionStorageKey,
  setPersonalizedWelcomeChatSessionId,
  setPersonalizedWelcomeChatSessionStorageKey,
} from 'bundles/ai-coach-platform/components/patterns/chat/store/dispatcher';
import type { OnReceiveMessage, SendMessageFunc } from 'bundles/ai-coach-platform/components/patterns/chat/types';
import { getCookieId, getDeviceId } from 'bundles/ai-coach-platform/components/patterns/chat/utils/logoutModeUtils';
import { isCoachMessage } from 'bundles/ai-coach-platform/components/patterns/chat/utils/messageUtils';
import type { Action as SuggestedAction } from 'bundles/ai-coach-platform/components/patterns/popover/types';
import { getErrorFromApolloError } from 'bundles/ai-coach-platform/utils/errors';
import { captureMessageInSentry } from 'bundles/ai-coach-platform/utils/sentry';
import type { CoachTrackingData } from 'bundles/ai-coach-platform/utils/tracking';
import { CoachTrackingDataProvider } from 'bundles/ai-coach-platform/utils/tracking';
import useHandleOnReceiveMessage from 'bundles/ai-coach-platform/utils/useHandleOnReceiveMessage';

import _t from 'i18n!nls/ai-coach-platform';

import { getErrorMessageText } from './constants';
import store from './store';
import type { GlobalState } from './store/types';
import {
  clearedPersonalizedWelcomeSessionStorageKeys,
  getChatSessionStorageKey,
  getContinuousChatSessionStorageKey,
  getPersonalizedWelcomeChatSessionStorageKey,
  persistentChatSessionId,
  persistentContinuousChatSessionId,
  persistentPersonalizedWelcomeChatSessionId,
} from './utils/sessionUtils';

const styles = {
  root: css`
    z-index: 3100;
    bottom: 0;
    position: absolute;
    left: 0;
    top: 0;
    margin: 0;
    right: 0;
    padding: var(--cds-spacing-200);

    ${breakpoints.down('xs')} {
      padding: 0;
    }

    ${breakpoints.up('md')} {
      [data-course-frame-2026] & {
        padding: 0;
      }
    }

    .coach-new-message {
      width: 100%;
    }

    .coach-input {
      height: 60px;
    }
  `,
  viewContainer: (settingsOpen: boolean) => css`
    position: relative;
    height: 100%;
    width: 100%;

    ${settingsOpen &&
    `
      /* hide conversation from screen-readers when settings open */
      .coach-conversation-container {
        display: none;
      }
    }`}
  `,
};

type CoachChatProps = {
  additionalStyles?: SerializedStyles;

  /** Context info needed for Coach interactions  */
  contextConfig?: Omit<AiCoachRespondToUserMessageInput, 'message' | 'config'> & { message?: undefined } & {
    config?: AiCoachRespondToUserMessageInput['config'];
  };

  /**
   * Coach mode to be used for the chat interaction
   */
  mode: string;

  emptyState?: ReactElement;

  /**
   * Custom key for identifying a chat session uniquely
   * If continuous chat is enabled,a unique string should be provided as the chatSessionKey prop to ensure sessions are managed correctly
   */
  chatSessionKey?: string;

  /**
   * Enable/disable storing the chat session id
   */
  storeSessionId?: boolean;

  /**
   * Chat session id for the active chat session
   * When this prop is passed, only messages from this chat session will be shown
   */
  activeChatSessionId?: string;

  /**
   * Enable/disable the text input field for sending messages
   */
  disableInput?: boolean;

  /**
   * Enable/disable the speech to text button in RichInputField
   */
  disableSpeechToText?: boolean;

  /**
   * Tooltip text for when the speech to text button in RichInputField is disabled
   */
  speechToTextDisabledTooltip?: string;

  /**
   * Callback for when Coach is prompted i.e. via input field or action button currently, or some other prompt in future
   * Can be also used for product-specific eventing.
   */
  onSend?: (action?: string) => void;

  /**
   * Callback for when chat is mounted
   * Can be also used for product-specific eventing
   */
  onMount?: () => void;

  /**
   * Callback for when Close button is clicked or chat is unmounted.
   * Can be also used for product-specific eventing
   */
  onClose?: () => void;

  /**
   * Callback for any API errors on the send message call
   */
  onSendError?: (apolloError: ApolloError) => void;

  /**
   * Callback for when a message is received, containing the latest Coach-sent message.
   * Useful for invoking other page-level actions through the chat interface
   */
  onReceiveMessage?: OnReceiveMessage;

  /**
   * Placeholder for the chat input field
   *
   * @default 'Ask me anything'
   */
  inputPlaceholder?: string;

  /**
   * Label for the chat input field
   */
  inputLabel?: string;

  /**
   * Custom header
   * Pass a React element to use a custom header, or null to hide the header
   * If undefined, the default header is used
   */
  customHeader?: ReactElement | null;

  /**
   * Custom footer
   * Pass a React element to use a custom footer, undefined or null to show the default footer.
   */
  customFooter?: ReactElement | null;

  /**
   * Whether to show the chat close button
   *
   * @default true
   */
  showCloseButton?: boolean;

  /**
   * Whether to show Coach Settings button in chat header and subsequent settings UI pages
   *
   * @default false
   */
  showSettings?: boolean;

  /**
   * Whether to show the "Start a new chat" button
   *
   * @default true
   */
  showStartNewChat?: boolean;

  /**
   * Callback for when a new chat is started
   */
  onStartNewChat?: () => void;

  /**
   * Whether to show the fullscreen expand/collapse button
   */
  showFullScreenButton?: boolean;

  /**
   * Hides the message in the chat. This function accepts an individual message and its index
   * returns a boolean value if the message needs to be hidden based on the function's implementation
   * at the parent level.
   * Eg: Hiding the first "initiation" message and chat summary message for coach item use case.
   *
   * @default undefined
   */
  hideMessage?: HideMessageType;

  /**
   * Whether to autofocus on the input field when chat mounts
   *
   * @default true
   */
  autofocus?: boolean;

  /**
   * Custom aria-label for the chat container
   * @default 'Coach chat container'
   */
  'aria-label'?: string;

  /**
   * Whether the chat is a Coach Studio instance
   */
  isCoachStudio?: boolean;

  /**
   * Toggles fullscreen view
   */
  toggleFullScreen?: () => void;

  /**
   * Whether the chat is in fullscreen view
   */
  isFullScreen?: boolean;

  /**
   * Maximum number of messages of chat history to query for
   * @default 5
   */
  chatHistoryLimit?: number;

  /**
   * Whether to show the position toggle button
   */
  showPositionToggle?: boolean;

  /**
   * Toggle the chat position when the position toggle button is clicked
   */
  handlePositionToggleClick?: () => void;

  /**
   * Brand label used throughout chat UI (e.g. "Coach" or "Coursera AI").
   * Defaults to 'Coach'
   */
  brandLabel?: string;

  /**
   * Whether to show the pop-in/out toggle button
   */
  chatPopupButton?: ChatPopupButton;

  /**
   * Call this when the pop-in/out button is clicked. This component does not handle the pop-in/out action itself.
   */
  handlePopupButtonClick?: () => void;

  /**
   * Whether to use the rich input field for sending chat messages, such as speech-to-text capabilities
   *
   * @default false
   */
  enableRichInputField?: boolean;

  /**
   * Custom input field renderer to override the default input field component in the chat footer.
   *
   * @default () => null
   */
  renderCustomInputField?: ({ sendMessage }: { sendMessage: SendMessageFunc }) => ReactElement | null;

  /**
   * Whether to enable support for >30s requests.
   *
   * Enable this only when absolutely necessary, as long LLM response times in a chat context may be a UX smell.
   *
   * Please see [the documentation on Confluence](https://coursera.atlassian.net/wiki/spaces/PE/pages/3830054990/Chat+UI) for more information
   */
  enableTaskBasedProcessing?: boolean;

  /**
   * Render prop for custom rendering chat messages. This callback will be executed for each message
   * in the chat, passing the data for that message, as well as all the messages so far if needed
   * for any additional custom logic.
   *
   * Conditional rendering: you can return `null` in this function to let platform render
   * with the default styling for any message. See the storybook for example usages.
   * If you need to skip rendering a message, see `hideMessage` prop.
   *
   * @default () => null
   */
  renderCustomMessage?: (props: ChatMessageProps) => ReactElement | null;

  /**
   * Render prop for custom rendering of the message loader.
   *
   * @default () => null
   */
  renderCustomMessageLoader?: (props: ProgressiveLoaderProps) => ReactElement | null;
  /**
   * Render prop for custom rendering of the temporary streamed message.
   *
   * @default () => null
   */
  renderCustomTemporaryStreamedMessage?: (props: ChatMessageBodyProps) => ReactElement | null;
  /**
   * Render prop for a custom temporary message that will be shown at the end of the chat conversation.
   * Note that this message is FE-only and is not part of the actual chat history saved to BE.
   *
   * @default () => null
   */
  renderTemporaryMessage?: () => ReactElement | null;

  /**
   * Aria-label for the chat message list
   */
  chatMessageListAriaLabel?: string;
  /**
   * Renders custom content in the composer area with a sliding transition.
   * Use this when you need to temporarily hide the default composer component
   * and show something more releveant to current chat context.
   *
   * e.g. a custom actions component to match the currently shown Coach message
   */
  dynamicComposer?: Pick<
    ConversationProps,
    | 'dynamicComposerContent'
    | 'showDynamicComposerContent'
    | 'onDynamicComposerContentShown'
    | 'onDefaultComposerContentShown'
  >;

  /**
   * If intention routing is enabled, the 'ROUTER' action will be used for messages sent from the chat input field
   *
   * @default false
   */
  enableIntentionRouting?: boolean;

  /**
   * Whether to use text streaming for chat messages.
   * Note that if you're using external actions to dispatch messages,
   * you also need to pass `enableTextStreaming: true` to the `sendMessage` function.
   */
  enableTextStreaming?: boolean;

  /**
   * Whether to enable the Find in chat search feature
   *
   * @default false
   */
  enableFindInChat?: boolean;

  /**
   * Whether to enable the continuous version of Coach chat
   *
   * @default false
   */
  enableContinuousChat?: boolean;

  /**
   * Whether Personalized Coach Welcome is enabled
   *
   * @default false
   */
  enablePersonalizedWelcome?: boolean;

  /**
   * Whether to enable logged out mode for Coach chat.
   * When enabled, the eventing device UUID will be passed to GraphQL queries for logged out Coach.
   *
   * Note: This only works when enableTextStreaming is also true, as the device ID is only added
   * for the subscriptions path in the dispatcher.
   *
   * @default false
   */
  enableLoggedOutMode?: boolean;

  /**
   * Whether to opt this Coach surface into the brand update (removes Coach branding from the chat experience).
   */
  enableBrandUpdate?: boolean;

  /**
   * Optional coach config version to use. If not provided, the latest published config is used.
   */
  coachConfigVersion?: number;

  /**
   * Suggested actions for "Ask" button to be displayed in the footer
   */
  suggestedActions?: SuggestedAction[] | undefined;

  chatPrefixContent?: ReactElement | null;

  /**
   * Whether to fetch chat history when the location (URL) changes.
   * Set to false when location changes shouldn't trigger a history refetch
   * (e.g., when query params update without visual page changes).
   *
   * @default true
   */
  fetchChatHistoryOnLocationChange?: boolean;

  /**
   * Custom configuration for the memory preference notification.
   * Use this to customize the title and content shown in the memory notification
   * for specific Coach instances (e.g., AdminCoach).
   */
  memoryNotificationConfig?: MemoryNotificationConfig;

  /**
   * Custom label text for recommended actions.
   * If not provided, uses the default text.
   * If an empty string, hides the label.
   * If a non-empty string, displays that text.
   */
  recommendedActionsLabel?: string;

  /**
   * When true, fetch chat history on mount even if a message send is
   * currently in-flight (messageLoader active). When false, skip fetching
   * to prevent server data from overwriting Redux messages that haven't
   * been persisted yet. Useful when transitioning from another view that
   * already sent a message.
   *
   * @default true
   */
  fetchChatHistoryOnInflightSend?: boolean;
};

/**
 * Main component for the Coach chat pattern.
 *
 * DO NOT use this component for actual product use cases, use the default import.
 * Only use this component for testing or storybook to override the providers.
 *
 * Redux: wrap this component with redux `<Provider>` to provide the store data. This will fail to load otherwise.
 *
 * Eventing: wrap this component with `<CoachTrackingDataProvider>` passing the necessary context values
 */
export const CoachChat = ({
  additionalStyles,
  contextConfig,
  mode,
  emptyState,
  chatSessionKey = '',
  storeSessionId = true,
  activeChatSessionId,
  inputPlaceholder = _t('Ask me anything'),
  inputLabel,
  customHeader,
  customFooter,
  showCloseButton = true,
  showStartNewChat = true,
  onStartNewChat,
  showSettings = false,
  showFullScreenButton = false,
  disableInput = false,
  disableSpeechToText = false,
  speechToTextDisabledTooltip = '',
  autofocus = true,
  enableRichInputField = false,
  hideMessage,
  onClose,
  onSendError,
  onSend,
  onMount,
  onReceiveMessage,
  'aria-label': ariaLabel,
  chatMessageListAriaLabel,
  isCoachStudio,
  toggleFullScreen,
  isFullScreen,
  chatHistoryLimit = 5,
  showPositionToggle,
  handlePositionToggleClick,
  brandLabel = 'Coach',
  chatPopupButton,
  handlePopupButtonClick,
  renderCustomInputField = () => null,
  enableTaskBasedProcessing,
  renderCustomMessage = () => null,
  renderCustomMessageLoader = () => null,
  renderCustomTemporaryStreamedMessage = () => null,
  renderTemporaryMessage = () => null,
  dynamicComposer = {},
  enableIntentionRouting = false,
  enableTextStreaming = false,
  enableFindInChat = false,
  enableContinuousChat = false,
  enablePersonalizedWelcome = false,
  enableLoggedOutMode = false,
  suggestedActions = [],
  chatPrefixContent = null,
  fetchChatHistoryOnLocationChange = true,
  memoryNotificationConfig,
  recommendedActionsLabel,
  fetchChatHistoryOnInflightSend = true,
  coachConfigVersion,
}: CoachChatProps) => {
  useHandleOnReceiveMessage(onReceiveMessage);
  const location = useLocation();

  const cookieId = useMemo(() => (enableLoggedOutMode ? getCookieId() : undefined), [enableLoggedOutMode]);
  const deviceId = useMemo(() => (enableLoggedOutMode ? getDeviceId() : undefined), [enableLoggedOutMode]);

  const {
    isSearchMode,
    exitSearchMode,
    setEarlierMessagesAvailable,
    setDisableProgrammaticScroll,
    setSuggestedActions,
    setChatHistoryCursor,
    setChatHistoryLimit,
    setChatPrefixContent,
    settingsOpen,
    openSettings,
    closeSettings,
  } = useCoachChatContext();

  // Create a stable location reference when fetchChatHistoryOnLocationChange is false
  // This prevents the chat history fetch effect from running on location changes
  const initialLocationRef = useRef(location);
  const effectiveLocation = fetchChatHistoryOnLocationChange ? location : initialLocationRef.current;
  const { sessionState } = store.getState();
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);

  const { showMessageLoader, messages } = useSelector((state: GlobalState) => ({
    showMessageLoader: state.behavior.messageLoader,
    messages: state.messages.messages,
  }));

  const isMounted = useRef<boolean>(false);

  const [showNewConversationModal, setShowNewConversationModal] = useState<boolean>(false);
  const { chatSessionId: chatSessionIdState, continuousChatSessionId: continuousChatSessionIdState } = sessionState;

  const {
    loading: isChatHistoryLoading,
    fetchChatHistory,
    fetchMoreChatHistory,
    earlierMessagesAvailable,
    cursor,
  } = useChatHistory({
    chatSessionId: enableContinuousChat ? continuousChatSessionIdState : chatSessionIdState,
    historyLimit: chatHistoryLimit,
    mode,
    cookieId,
    deviceId,
  });

  useEffect(() => {
    setEarlierMessagesAvailable(earlierMessagesAvailable);
  }, [earlierMessagesAvailable, setEarlierMessagesAvailable]);

  useEffect(() => {
    if (enableContinuousChat && suggestedActions.length > 0) {
      setSuggestedActions?.(suggestedActions);
    }
  }, [enableContinuousChat, suggestedActions, setSuggestedActions]);

  useEffect(() => {
    setChatHistoryCursor(cursor);
  }, [cursor, setChatHistoryCursor]);

  useEffect(() => {
    setChatHistoryLimit(chatHistoryLimit);
  }, [chatHistoryLimit, setChatHistoryLimit]);

  useEffect(() => {
    setChatPrefixContent(chatPrefixContent);
  }, [chatPrefixContent, setChatPrefixContent]);

  const getSessionStorageKey = useCallback<() => string>(() => {
    return getChatSessionStorageKey(effectiveLocation, chatSessionKey);
  }, [effectiveLocation, chatSessionKey]);

  const getContinuousSessionStorageKey = useCallback<() => string>(() => {
    // If continuous chat is being enabled outside of course context (no courseId or courseSlug from location),
    // the chatSessionKey prop will be used as the uniqueDataString parameter for session storage.
    // When both courseId and courseSlug are unavailable, a unique string should be provided as the chatSessionKey prop to ensure continuous chat sessions are managed correctly.
    const courseId = contextConfig?.courseraContext?.courseId || undefined;
    return getContinuousChatSessionStorageKey(effectiveLocation, courseId, chatSessionKey);
  }, [effectiveLocation, chatSessionKey, contextConfig]);

  const getPersonalizedWelcomeSessionStorageKey = useCallback<() => string>(() => {
    const courseId = contextConfig?.courseraContext?.courseId || undefined;
    return getPersonalizedWelcomeChatSessionStorageKey(effectiveLocation, courseId);
  }, [effectiveLocation, contextConfig]);

  useEffect(() => {
    if (isMounted.current === false) {
      isMounted.current = true;
      onMount?.();
    }

    return () => {
      isMounted.current = false;
    };
  }, [onMount]);

  useEffect(() => {
    // auto-close Settings when a new message is sent
    // (only from external actions currently, such as Disclosure or ExternalCoachAction)
    if (settingsOpen && showMessageLoader) {
      closeSettings();
    }
  }, [showMessageLoader, settingsOpen, closeSettings]);

  useEffect(() => {
    // auto-close search mode when a new message is sent
    if (enableFindInChat && isSearchMode() && showMessageLoader) {
      exitSearchMode();
    }
  }, [enableFindInChat, isSearchMode, showMessageLoader, exitSearchMode]);

  useEffect(() => {
    if (storeSessionId) {
      let sessionStorageKey: string = '';
      let sanitizedChatSessionId: string | undefined;

      // fetch any existing continuous chat session from local storage
      const continuousSessionStorageKey = getContinuousSessionStorageKey();
      const localStorageSessionId = persistentContinuousChatSessionId.get(continuousSessionStorageKey);

      // fetch any existing session from session storage
      const nonContinuousSessionStorageKey = getSessionStorageKey();
      const sessionStorageSessionId = persistentChatSessionId.get(nonContinuousSessionStorageKey);

      if (enablePersonalizedWelcome) {
        /*
         * For the Personalized Coach Welcome experience, Coach will initiate a chat with the learner on the first N items
         * The initial chats will be stored in sessionStorage as separate key-value pairs:
         *   sessionStorage["activeCourseraCoachSessionId-example-1"] = "example_session_id_1"
         *   sessionStorage["activeCourseraCoachSessionId-example-2"] = "example_session_id_2"
         * Once a learner interacts with one of the chats, it will become the active personalized welcome session
         * For this active personalized welcome session, we take the sessionId from sessionStorage and also add it to localStorage as the active personalized welcome session:
         *   localStorage["activeCourseraCoachPersonalizedWelcomeSessionId-example-1"] = ""example_session_id_1"
         * Any other initial personalized welcome chats that were not interacted with will then be removed from sessionStorage the next time each chat is loaded
         */
        const personalizedWelcomeSessionStorageKey = getPersonalizedWelcomeSessionStorageKey();
        const personalizedWelcomeSessionId = persistentPersonalizedWelcomeChatSessionId.get(
          personalizedWelcomeSessionStorageKey
        );
        if (personalizedWelcomeSessionId && personalizedWelcomeSessionId === sessionStorageSessionId) {
          // Scenario 1: There is an active personalized welcome session on the current item
          sanitizedChatSessionId = personalizedWelcomeSessionId;
          sessionStorageKey = personalizedWelcomeSessionStorageKey;
          setPersonalizedWelcomeChatSessionStorageKey(sessionStorageKey);
          setPersonalizedWelcomeChatSessionId(sanitizedChatSessionId);
          setChatSessionStorageKey(nonContinuousSessionStorageKey);
          setChatSessionId(sessionStorageSessionId);
        } else if (
          personalizedWelcomeSessionId &&
          personalizedWelcomeSessionId !== sessionStorageSessionId &&
          !clearedPersonalizedWelcomeSessionStorageKeys.has(nonContinuousSessionStorageKey)
        ) {
          // Scenario 2: There is an active personalized welcome session on a different item and the current item has a stale initial personalized welcome message that should be cleared
          setChatSessionStorageKey(nonContinuousSessionStorageKey);
          clearChatSession();
          dropMessages();
          clearedPersonalizedWelcomeSessionStorageKeys.add(nonContinuousSessionStorageKey);
        } else {
          // Scenario 3: There is no active personalized welcome session
          sanitizedChatSessionId = sessionStorageSessionId;
          setChatSessionStorageKey(nonContinuousSessionStorageKey);
          setChatSessionId(sanitizedChatSessionId);
        }
      } else if (enableContinuousChat) {
        // local storage session always takes precedence over session storage session when continuous chat is enabled
        if (localStorageSessionId) {
          sanitizedChatSessionId = localStorageSessionId;
        } else if (sessionStorageSessionId) {
          // when there isn't an existing continuous session but there is an existing session
          // we will reuse that session as the new continuous session for a seamless experience
          // so users can continue the conversation from where they left off
          sanitizedChatSessionId = sessionStorageSessionId;
        }

        sessionStorageKey = continuousSessionStorageKey;
        setContinuousChatSessionStorageKey(sessionStorageKey);
        setContinuousChatSessionId(sanitizedChatSessionId);
      } else if (localStorageSessionId) {
        // If there is an existing continuous chat session and the continuous chat experiment is not enabled, the chat should be converted to a non-continuous chat session and the continuous chat session should be cleared
        sanitizedChatSessionId = localStorageSessionId;
        setContinuousChatSessionStorageKey(continuousSessionStorageKey);
        clearContinuousChatSession();

        sessionStorageKey = nonContinuousSessionStorageKey;
        setChatSessionStorageKey(sessionStorageKey);
        setChatSessionId(sanitizedChatSessionId);
        persistentChatSessionId.set(sessionStorageKey, sanitizedChatSessionId);
      } else {
        sanitizedChatSessionId = sessionStorageSessionId;
        setChatSessionStorageKey(nonContinuousSessionStorageKey);
        setChatSessionId(sanitizedChatSessionId);
      }

      // When fetchChatHistoryOnInflightSend is false, skip fetching chat
      // history if a message send is in-flight and the store already has
      // messages. This prevents server data from overwriting Redux messages
      // that haven't been persisted yet (e.g. transitioning from the
      // onboarding dialog to left-aligned chat).
      const shouldSkipFetchChatHistory =
        fetchChatHistoryOnInflightSend === false && showMessageLoader && messages.length > 0;

      // if we already have a session saved in SessionStorage, fetch it by the sessionId
      if (sanitizedChatSessionId) {
        if (!shouldSkipFetchChatHistory) {
          fetchChatHistory?.(sanitizedChatSessionId);
        }
      } else {
        // if we don't have a session, we must clear the store so that we don't show
        // messages from another session (e.g. switching from one item page to another)
        const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;

        // [COACH-1216] only clear messages if the latest message is NOT a client message awaiting
        // a Coach response. This typically happens when CoachChat gets mounted as a result of
        // an external action (dislosure button), so when this effect is called we want to preserve
        // that message and not drop it. And if there aren't any messages yet, we don't need to clear anything.
        if (lastMessage && isCoachMessage(lastMessage.sender)) {
          dropMessages();
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveLocation, storeSessionId, fetchChatHistory]);

  useEffect(() => {
    return () => {
      // On component unmount, if we are not storing the session ID, clear the messages
      if (!storeSessionId) {
        if (enableContinuousChat) {
          clearContinuousChatSession();
        } else {
          clearChatSession();
        }

        dropMessages();
      }
    };
  }, [storeSessionId, enableContinuousChat]);

  const handleFetchChatHistory = React.useCallback<(sessionId: string) => Promise<UpdateChatHistory['messages']>>(
    async (sessionId: string) => {
      return fetchChatHistory?.(sessionId, true);
    },
    [fetchChatHistory]
  );

  const { setIsWindowFocusListenerActive } = useFetchChatHistoryOnVisibilityChange({
    enableContinuousChat,
    fetchChatHistory: handleFetchChatHistory,
    location,
    courseId: contextConfig?.courseraContext?.courseId || undefined,
    chatSessionKey,
  });

  const handleClose = () => {
    onClose?.();
  };

  const handleSendMessageError = (apolloError: ApolloError) => {
    onSendError?.(apolloError);

    enableMsgLoader(false);
    addResponseMessage({ text: getErrorMessageText() });
    const error = getErrorFromApolloError(apolloError);
    if (error) {
      captureMessageInSentry({ error, coachMode: mode, source: 'sendMessage' });
    }
  };

  const handleNewContinuousSession = () => {
    setShowNewConversationModal(true);
  };

  const handleNewSession = () => {
    if (enableContinuousChat) {
      // clear the existing continuousChatSessionId from local storage
      clearContinuousChatSession();
      const sessionStorageKey = getContinuousSessionStorageKey();
      setContinuousChatSessionStorageKey(sessionStorageKey);
    } else {
      // clear the chatSessionId from the session storage
      clearChatSession();
      // clear recommended action click timestamp from session storage
      const sessionStorageKey = getSessionStorageKey();
      setChatSessionStorageKey(sessionStorageKey);
    }
    dropMessages();
    onStartNewChat?.();
  };
  const handleFocusOnHeaderButton = (buttonRef: React.RefObject<HTMLButtonElement>) => {
    // a11y: since the input field takes focus by default, we need to wait and re-focus the header button
    setTimeout(() => buttonRef.current?.focus(), 0);
  };

  const handleSettingsClose = ({ isChatHistoryDeleted = false }: { isChatHistoryDeleted: boolean }) => {
    closeSettings();
    handleFocusOnHeaderButton(settingsButtonRef);

    if (isChatHistoryDeleted) {
      handleNewSession();
    }
  };

  const handleFetchMoreChatHistory = async () => {
    // Disable the window focus listener while fetching more chat history
    // This prevents fetching history multiple times and prevents firing the view_coach_earlier_messages event unexpectedly when the "Earlier Messages" button is clicked
    setIsWindowFocusListenerActive(false);
    await fetchMoreChatHistory();
    setDisableProgrammaticScroll(true);
    setIsWindowFocusListenerActive(true);
  };

  return (
    <div
      data-testid="ai-coach-chat-container"
      css={[styles.root, additionalStyles]}
      aria-label={ariaLabel ?? _t('Coach chat container')}
    >
      <div css={styles.viewContainer(settingsOpen)}>
        <Conversation
          showSearchButton={enableFindInChat}
          hideComposer={isSearchMode()}
          showSettings={showSettings}
          renderCustomInputField={renderCustomInputField}
          enableRichInputField={enableRichInputField}
          enableTextStreaming={enableTextStreaming}
          handleSettingsClick={openSettings}
          emptyState={emptyState}
          hideMessage={hideMessage}
          chatMessageListAriaLabel={chatMessageListAriaLabel}
          customHeader={
            isSearchMode() ? (
              <FindInChatSearch onClose={() => handleFocusOnHeaderButton(searchButtonRef)} />
            ) : (
              customHeader
            )
          }
          searchButtonRef={searchButtonRef}
          settingsButtonRef={settingsButtonRef}
          activeChatSessionId={activeChatSessionId}
          sendMessage={(message: string, action: string) => {
            let actionId = action;

            // recommended actions (GENERIC_ACTION) are only supported in ITEM_LEARNING mode,
            // so we need to fallback to TYPED_MESSAGE if this is used in other modes, when in continuous chat
            if (enableContinuousChat && action === 'GENERIC_ACTION' && mode !== 'ITEM_LEARNING') {
              actionId = 'TYPED_MESSAGE';
            }
            const finalActionId = actionId === 'TYPED_MESSAGE' && enableIntentionRouting ? 'ROUTER' : actionId;

            setDisableProgrammaticScroll(false);
            onSend?.(finalActionId);
            dispatchSendMessage({
              input: {
                message,
                config: {
                  coachActionId: finalActionId,
                  coachModeId: mode,
                  ...(coachConfigVersion !== undefined && { coachConfigVersion }),
                },
                ...contextConfig,
                responseProcessingType: enableTaskBasedProcessing ? 'TASK_BASED_PROCESSING' : undefined,
              },
              enableTextStreaming,
              enableContinuousChat,
              cookieId,
              deviceId,
            }).catch(handleSendMessageError);
          }}
          senderPlaceHolder={inputPlaceholder}
          inputLabel={inputLabel}
          toggleChat={handleClose} // always close because if you can see the coach close button, it will always close
          showCloseButton={showCloseButton}
          showStartNewChat={showStartNewChat}
          disableInput={disableInput}
          disableSpeechToText={disableSpeechToText}
          speechToTextDisabledTooltip={speechToTextDisabledTooltip}
          autofocus={autofocus}
          handleNewSession={enableContinuousChat ? handleNewContinuousSession : handleNewSession}
          isChatHistoryLoading={isChatHistoryLoading}
          customFooter={isSearchMode() ? <SearchFooter /> : customFooter}
          isCoachStudio={isCoachStudio}
          showFullScreenButton={showFullScreenButton}
          toggleFullScreen={toggleFullScreen}
          isFullScreen={isFullScreen}
          showPositionToggle={showPositionToggle}
          handlePositionToggleClick={handlePositionToggleClick}
          chatPopupButton={chatPopupButton}
          handlePopupButtonClick={handlePopupButtonClick}
          renderCustomMessage={renderCustomMessage}
          renderCustomMessageLoader={renderCustomMessageLoader}
          renderCustomTemporaryStreamedMessage={renderCustomTemporaryStreamedMessage}
          renderTemporaryMessage={renderTemporaryMessage}
          enableContinuousChat={enableContinuousChat}
          onFetchMoreChatHistory={handleFetchMoreChatHistory}
          recommendedActionsLabel={recommendedActionsLabel}
          memoryNotificationConfig={memoryNotificationConfig}
          brandLabel={brandLabel}
          {...dynamicComposer}
        />
        {settingsOpen ? (
          <SettingsContextProvider
            settingsEnabled={showSettings}
            enableContinuousChat={enableContinuousChat}
            brandLabel={brandLabel}
          >
            <SettingsHome onClose={handleSettingsClose} isCoachStudio={isCoachStudio} />
          </SettingsContextProvider>
        ) : null}
        {showNewConversationModal ? (
          <NewConversationModal onClose={() => setShowNewConversationModal(false)} onSubmit={handleNewSession} />
        ) : null}
      </div>
    </div>
  );
};

export type Props = CoachChatProps & {
  // Merged with `mode` from `props.mode` inside `CoachTrackingDataProvider`; may omit `mode`.
  trackingData?: Partial<CoachTrackingData['data']>;
};

/**
 * Wrapper component for `CoachChat` that provides the Redux store and more contexts like
 * eventing and UI state management.
 */
const CoachChatWithStore = (props: Props) => {
  const { trackingData, enableLoggedOutMode, enableBrandUpdate } = props;

  return (
    <StoreProviderWrapper>
      <CoachTrackingDataProvider value={{ data: { ...trackingData, mode: props.mode || 'UNKNOWN_UNDEFINED' } }}>
        <CoachChatContextProvider enableLoggedOutMode={enableLoggedOutMode} enableBrandUpdate={enableBrandUpdate}>
          <CoachChat {...props} />
        </CoachChatContextProvider>
      </CoachTrackingDataProvider>
    </StoreProviderWrapper>
  );
};
export default CoachChatWithStore;
